/**
 * analytics — thin wrapper around the Google tag (gtag.js, GA4).
 *
 * The tag itself is loaded in public/index.html with Consent Mode v2
 * defaults (everything denied until the visitor accepts). This module only
 * pushes events, so every call is a safe no-op when gtag is missing
 * (ad blockers, tests, SSR prerender).
 *
 * Rules:
 *  - Never send PII. Emails, names, phones and addresses stay out of every
 *    payload, and the `email` query param is stripped from page_location.
 *  - Money values are INR numbers, not strings.
 *  - Event names follow GA4 recommended ecommerce events so the built-in
 *    Monetisation reports fill in without custom setup.
 */

const CONSENT_KEY = "karijeeva_consent_v1";
const PII_PARAMS = ["email", "phone", "name", "token"];

/**
 * True while scripts/prerender.mjs snapshots the page in headless Chromium
 * (it sets window.__PRERENDER__ before the app loads). No events, consent
 * updates or page views may leave the build machine.
 */
export function isPrerender() {
  return typeof window !== "undefined" && window.__PRERENDER__ === true;
}

function gtag() {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  if (isPrerender()) return;
  // eslint-disable-next-line prefer-rest-params
  window.gtag.apply(window, arguments);
}

export function track(name, params = {}) {
  if (isPrerender()) return;
  gtag("event", name, params);
}

// ---------- consent ----------

export function getConsent() {
  try { return localStorage.getItem(CONSENT_KEY); } catch { return null; }
}

export function setConsent(granted) {
  const value = granted ? "granted" : "denied";
  try { localStorage.setItem(CONSENT_KEY, value); } catch {}
  gtag("consent", "update", {
    analytics_storage: value,
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
}

export function clearConsent() {
  try { localStorage.removeItem(CONSENT_KEY); } catch {}
}

// ---------- page views ----------

export function cleanLocation(href = window.location.href) {
  try {
    const url = new URL(href);
    PII_PARAMS.forEach((p) => url.searchParams.delete(p));
    return url.toString();
  } catch {
    return href;
  }
}

/**
 * Point gtag at the cleaned URL right away, before any page event can fire.
 * gtag otherwise reads document.location, which can hold ?email=.
 */
export function setPageLocation() {
  gtag("set", { page_location: cleanLocation() });
}

/** page_location is captured by the caller when the route starts, because
 * window.location has already moved on if the view is flushed on leaving. */
export function trackPageView(page_location, page_title = document.title) {
  gtag("set", { page_location, page_title });
  track("page_view", { page_location, page_title });
}

// ---------- ecommerce ----------

const BRAND = "Karijeeva";

/** Cart line / order line / variant → GA4 item. */
export function toItem(src, extra = {}) {
  const price = Number(src.unit_price ?? src.price ?? 0);
  const mrp = Number(src.mrp ?? 0);
  const item = {
    item_id: src.sku || src.variant_id || src.id,
    item_name: src.product_name || src.name,
    item_brand: BRAND,
    item_category: "Coconut Oil",
    item_variant: src.variant_size || src.size,
    price,
    quantity: Number(src.quantity || 1),
    ...extra,
  };
  if (mrp > price) item.discount = Math.round((mrp - price) * 100) / 100;
  return item;
}

export function productToItems(product, variant, extra = {}) {
  const variants = variant ? [variant] : product.variants || [];
  return variants.map((v) =>
    toItem({ ...v, product_name: product.name, variant_id: v.id }, {
      item_category2: product.category,
      ...extra,
    }),
  );
}

const value = (items) =>
  Math.round(items.reduce((s, i) => s + i.price * i.quantity, 0) * 100) / 100;

export function trackViewItemList(listName, products) {
  const items = products.map((p, index) => {
    const v = p.variants?.[0] || {};
    return toItem({ ...v, product_name: p.name, variant_id: v.id }, {
      index,
      item_list_name: listName,
      item_category2: p.category,
    });
  });
  if (items.length) track("view_item_list", { item_list_name: listName, items });
}

export function trackSelectItem(listName, product, index) {
  const [item] = productToItems(product, product.variants?.[0], { index, item_list_name: listName });
  if (item) track("select_item", { item_list_name: listName, items: [item] });
}

export function trackViewItem(product, variant) {
  const items = productToItems(product, variant);
  track("view_item", { currency: "INR", value: value(items), items });
}

export function trackCart(name, lines, extra = {}) {
  const items = lines.map((l) => toItem(l));
  track(name, { currency: "INR", value: value(items), items, ...extra });
}

export function trackPurchase(order) {
  const txn = order.order_number || order.id;
  // One purchase per order even if the confirmation page is reloaded.
  const key = `karijeeva_ga_purchase_${txn}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
  } catch {}
  track("purchase", {
    transaction_id: txn,
    currency: "INR",
    value: Number(order.total || 0),
    tax: Number(order.gst_amount || 0),
    shipping: Number(order.shipping_amount || 0),
    coupon: order.coupon_code || undefined,
    items: (order.items || []).map((i) => toItem(i)),
  });
}
